import {
  FieldControl,
  FieldFooter,
  FieldLabel,
  FieldShell,
} from "../../field-ui";
import type { ChoiceRow } from "../../../../lib/forms/fieldOptions";
import { labeledChoiceFromRows } from "../../../../lib/forms/fieldOptions";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { FormFieldInputProps, FormFieldRef } from "../../types";
import { reportFieldValue } from "../../../../lib/forms/reportFieldValue";

interface ChoiceRadioTilesInputProps extends FormFieldInputProps {
  placeholder: string;
  labelMuted?: boolean;
  className?: string;
  footertext?: string;
  choiceRows: ChoiceRow[];
  disabled?: boolean;
  validation?: (value: string) => boolean;
}

const ChoiceRadioTilesInput = forwardRef<
  FormFieldRef,
  ChoiceRadioTilesInputProps
>((props, ref) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState(props.initialValue ?? "");
  const [validationState, setValidationState] = useState(true);

  const commitValue = (next: string) => {
    setValue(next);
    reportFieldValue(props.fieldName, next, props.onValueChange);
  };

  useEffect(() => {
    if (
      value &&
      props.choiceRows.length > 0 &&
      !props.choiceRows.some((row) => row.value === value)
    ) {
      commitValue("");
      setValidationState(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when options/value mismatch
  }, [props.choiceRows, value]);

  const clear = () => {
    commitValue("");
  };

  const validate = () => {
    if (!props.validation) return true;
    const result = props.validation(value);
    setValidationState(result);
    return result;
  };

  useImperativeHandle(ref, () => ({
    setValidationState,
    value,
    scrollToElement: (options) => {
      shellRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        ...options,
      });
    },
    fieldId: props.fieldId,
    fieldName: props.fieldName,
    labeledChoice: labeledChoiceFromRows(props.choiceRows, value),
    validate,
    clear,
  }));

  const handleSelect = (row: ChoiceRow) => {
    if (props.disabled) return;
    commitValue(row.value);
    setValidationState(true);
  };

  return (
    <FieldShell ref={shellRef} className={props.className}>
      <FieldLabel muted={props.labelMuted}>{props.placeholder}</FieldLabel>
      <FieldControl className="min-h-0">
        <div
          role="radiogroup"
          aria-label={
            props.placeholder.trim() || props.accessibleName || undefined
          }
          aria-invalid={!validationState}
          className={[
            "grid gap-2 sm:grid-cols-2",
            props.disabled ? "pointer-events-none opacity-60" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {props.choiceRows.map((row) => {
            const checked = value === row.value;
            const inputId = `${props.fieldId}-${row.value}`;
            return (
              <label
                key={`${row.value}-${row.label}`}
                htmlFor={inputId}
                className={[
                  "form-choice-tile flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 font-onest text-[15px] leading-snug transition-[border-color,background-color,box-shadow] duration-200",
                  checked
                    ? "border-brand-500 bg-brand-50 text-slate-900 shadow-sm shadow-brand-500/10 ring-1 ring-brand-500/15"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50",
                  !validationState && !checked ? "border-red-200" : "",
                  !validationState && checked
                    ? "border-red-400 ring-red-400/20"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <input
                  id={inputId}
                  type="radio"
                  name={props.fieldName}
                  value={row.value}
                  checked={checked}
                  disabled={props.disabled}
                  onChange={() => handleSelect(row)}
                  className="form-choice-tile__radio h-4 w-4 shrink-0 border-slate-300 text-brand-600 focus:ring-brand-500/30"
                />
                <span className="min-w-0 flex-1">{row.label}</span>
              </label>
            );
          })}
        </div>
      </FieldControl>
      <FieldFooter>
        {!validationState ? (
          <span className="text-red-600" role="alert">
            Выберите значение
          </span>
        ) : props.footertext ? (
          <span className="text-slate-500">{props.footertext}</span>
        ) : null}
      </FieldFooter>
    </FieldShell>
  );
});

ChoiceRadioTilesInput.displayName = "ChoiceRadioTilesInput";

export { ChoiceRadioTilesInput };
export type { ChoiceRadioTilesInputProps };
